import { useEffect, useMemo, useState } from "react";
import { Link, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import {
  ArrowLeft,
  ArrowRight,
  BriefcaseBusiness,
  Clock,
  Eye,
  EyeOff,
  Search,
  ShieldCheck,
} from "lucide-react";
import {
  isApiConfigured,
  patchAuthAccountMobile,
  postSignin,
  postSignup,
  postVerifyTurnstileToken,
} from "../apiClient";
import { buildAuthHrefWithNext, parseSafeInternalNextPath } from "../navigateAfterAuth";
import TurnstileField from "../components/TurnstileField";
import {
  isTurnstileEnabled,
  TURNSTILE_REQUIRED_MESSAGE,
  formatTurnstileErrorForUser,
} from "../turnstileConfig";
import PageSeo from "../components/PageSeo";
import {
  COUNTRY_DIAL_OPTIONS,
  getCountryIsoFromDial,
  getDialCodeForCountryIso,
} from "../countryDialOptions";
import { fetchSuggestedSignupCountryIso } from "../lib/suggestedSignupCountryIso";
import Nd1AuthShell from "../nd1/components/Nd1AuthShell";
import Nd1SignupMarketing from "../nd1/components/Nd1SignupMarketing";
import { isPrivatePreviewActive } from "../lib/sitePreview";
import "../nd1/nd1.css";

/** Align with API `users` columns and sensible UX caps (email VARCHAR(320), etc.). */
const SIGNUP_INPUT_MAX = {
  fullName: 200,
  email: 320,
  mobile: 24,
  companyName: 200,
  companyWebsite: 2048,
  password: 128,
};

const COMPANY_WEBSITE_PREFIX = "http://";

function stripLeadingUrlScheme(raw) {
  return String(raw ?? "").trim().replace(/^https?:\/\//i, "");
}

/** Scheme-less URL sent to API / navigation state (prefix shown outside the input). */
function buildCompanyWebsiteForSubmit(remainder) {
  const body = stripLeadingUrlScheme(remainder).trim();
  return body || "";
}

const COMPANY_WEBSITE_INPUT_MAX = SIGNUP_INPUT_MAX.companyWebsite;

function SignupPage({ onSignIn, currentUser, authToken }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const typeParam = searchParams.get("type");
  const safeNext = useMemo(() => parseSafeInternalNextPath(searchParams.get("next")), [searchParams]);
  const signinHref = useMemo(() => buildAuthHrefWithNext("/signin", safeNext), [safeNext]);
  const verifyMobilePath = useMemo(
    () => {
      const base = "/verify-mobile";
      return safeNext ? `${base}?next=${encodeURIComponent(safeNext)}` : base;
    },
    [safeNext]
  );
  const stateSignupData = location.state?.signupData;
  /** After email verification, user may return here only to fix mobile — email stays fixed. */
  const emailLocked = Boolean(stateSignupData?.emailVerified);
  const allowedSignupTypes = ["applicant", "hiring-manager"];
  const validType =
    typeParam === "hiring-manager" || typeParam === "recruiter"
      ? "hiring-manager"
      : "applicant";
  const stateType = stateSignupData?.signupType;
  const normalizedStateType =
    stateType === "recruiter" ? "hiring-manager" : stateType;
  const initialSignupType = allowedSignupTypes.includes(normalizedStateType)
    ? normalizedStateType
    : validType;
  const [signupType, setSignupType] = useState(initialSignupType);
  const [fullName, setFullName] = useState(stateSignupData?.fullName || "");
  const [email, setEmail] = useState(stateSignupData?.email || "");
  const [countryIso, setCountryIso] = useState(() => {
    const s = stateSignupData;
    if (!s) return "GB";
    if (typeof s.countryIso === "string" && /^[A-Za-z]{2}$/.test(s.countryIso.trim())) {
      return s.countryIso.trim().toUpperCase();
    }
    return getCountryIsoFromDial(s.countryCode) || "GB";
  });
  const [mobile, setMobile] = useState(stateSignupData?.mobile || "");
  const [companyName, setCompanyName] = useState(stateSignupData?.companyName || "");
  const [companyWebsite, setCompanyWebsite] = useState(() =>
    stripLeadingUrlScheme(stateSignupData?.companyWebsite || "")
  );
  const [password, setPassword] = useState(stateSignupData?.password || "");
  const [showPassword, setShowPassword] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [turnstileToken, setTurnstileToken] = useState(null);
  const [turnstileWidgetError, setTurnstileWidgetError] = useState(false);
  const [acceptedLegal, setAcceptedLegal] = useState(Boolean(stateSignupData?.acceptedLegal));

  /** Cloudflare edge country when no signupData from navigation (e.g. fresh signup). */
  useEffect(() => {
    if (stateSignupData) return;
    let cancelled = false;
    (async () => {
      const iso = await fetchSuggestedSignupCountryIso();
      if (!cancelled && iso) setCountryIso(iso);
    })();
    return () => {
      cancelled = true;
    };
  }, [stateSignupData]);

  const needsCompanyFields = signupType === "hiring-manager";

  const blockedDomains = useMemo(
    () => [
      "gmail.com",
      "hotmail.com",
      "outlook.com",
      "yahoo.com",
      "icloud.com",
      "aol.com",
      "proton.me",
      "protonmail.com",
    ],
    []
  );

  const hasPublicEmailDomain = (value) => {
    const parts = value.toLowerCase().split("@");
    if (parts.length !== 2) return false;
    return blockedDomains.includes(parts[1]);
  };

  const isBusinessEmailValid = (value) => {
    if (!needsCompanyFields) return true;
    if (!value) return false;
    return !hasPublicEmailDomain(value);
  };

  const cardSubtitle =
    signupType === "hiring-manager"
      ? "Hire real people. Cut through the noise."
      : "Find genuine jobs. Show the real you.";

  const showNd1Turnstile = isTurnstileEnabled() && !emailLocked;

  const onEmailChange = (event) => {
    const value = event.target.value;
    setEmail(value);

    if (needsCompanyFields && value && hasPublicEmailDomain(value)) {
      event.target.setCustomValidity(
        "Use your company email address. Public domains are not allowed for this sign up type."
      );
    } else {
      event.target.setCustomValidity("");
    }
  };

  const onSubmit = async (event) => {
    event.preventDefault();
    const form = event.currentTarget;
    const emailInput = form.elements.email;
    const normalizedEmail = email.trim().toLowerCase();
    setSubmitError("");

    if (!emailLocked && needsCompanyFields && hasPublicEmailDomain(email)) {
      emailInput.setCustomValidity(
        "Use your company email address. Public domains are not allowed for this sign up type."
      );
    } else {
      emailInput.setCustomValidity("");
    }

    if (!emailLocked && !form.checkValidity()) {
      form.reportValidity();
      return;
    }

    if (emailLocked) {
      if (!String(mobile || "").trim()) {
        setSubmitError("Enter your mobile number.");
        return;
      }
      if (isApiConfigured()) {
        if (!String(authToken || "").trim()) {
          setSubmitError(
            "Your session expired. Open sign up again from the mobile verification page, or sign in."
          );
          return;
        }
        setSubmitting(true);
        try {
          const { user } = await patchAuthAccountMobile({
            countryCode: getDialCodeForCountryIso(countryIso),
            mobile,
            authToken,
          });
          onSignIn(user);
          navigate(verifyMobilePath, {
            state: {
              signupData: {
                signupType,
                fullName,
                email,
                countryCode: getDialCodeForCountryIso(countryIso),
                countryIso,
                mobile,
                companyName,
                companyWebsite: buildCompanyWebsiteForSubmit(companyWebsite),
                password,
                emailVerified: true,
              },
            },
          });
        } catch (err) {
          setSubmitError(err instanceof Error ? err.message : "Update failed");
        } finally {
          setSubmitting(false);
        }
        return;
      }
      navigate(verifyMobilePath, {
        state: {
          signupData: {
            signupType,
            fullName,
            email,
            countryCode: getDialCodeForCountryIso(countryIso),
            countryIso,
            mobile,
            companyName,
            companyWebsite: buildCompanyWebsiteForSubmit(companyWebsite),
            password,
            emailVerified: true,
          },
        },
      });
      return;
    }

    if (!emailLocked && !acceptedLegal) {
      setSubmitError("Please agree to the Terms of Use and Privacy Policy to continue.");
      return;
    }

    if (!emailLocked && isTurnstileEnabled() && isApiConfigured()) {
      if (!turnstileToken) {
        setSubmitError(TURNSTILE_REQUIRED_MESSAGE);
        return;
      }
      setSubmitting(true);
      try {
        await postVerifyTurnstileToken(turnstileToken);
      } catch (err) {
        const raw = err instanceof Error ? err.message : "Verification failed";
        setSubmitError(formatTurnstileErrorForUser(raw));
        setSubmitting(false);
        return;
      }
      setSubmitting(false);
    }

    if (!emailLocked && isApiConfigured()) {
      setSubmitting(true);
      try {
        const result = await postSignup({
          email,
          password,
          fullName,
          signupType,
          countryCode: getDialCodeForCountryIso(countryIso),
          mobile,
          companyName: needsCompanyFields ? companyName : "",
          companyWebsite: needsCompanyFields ? buildCompanyWebsiteForSubmit(companyWebsite) : "",
          acceptTerms: acceptedLegal,
        });
        onSignIn(result.user, { token: result.token });
      } catch (err) {
        setSubmitError(err instanceof Error ? err.message : "Sign up failed");
        return;
      } finally {
        setSubmitting(false);
      }
    }

    const verifyEmailQs = new URLSearchParams();
    verifyEmailQs.set("email", email);
    if (safeNext) verifyEmailQs.set("next", safeNext);
    navigate(`/verify-email?${verifyEmailQs.toString()}`, {
      state: {
        signupData: {
          signupType,
          fullName,
          email,
          countryCode: getDialCodeForCountryIso(countryIso),
          countryIso,
          mobile,
          companyName,
          companyWebsite: needsCompanyFields ? buildCompanyWebsiteForSubmit(companyWebsite) : "",
          password,
        },
      },
    });
  };

  const signupCard = (
    <section className="nd1-auth-card nd1-auth-card--signup">
      <Link className="nd1-auth-back" to="/">
        <ArrowLeft className="nd1-icon" aria-hidden="true" />
        Back to home
      </Link>
      <p className="nd1-auth-card__eyebrow">Create account</p>
      <h2>Create your account</h2>
      <p className="nd1-auth-card__lede">{cardSubtitle}</p>
      {!emailLocked ? (
        <p className="nd1-signup-time">
          <Clock className="nd1-icon" aria-hidden="true" />
          Takes less than 2 minutes
        </p>
      ) : null}

      {submitError && submitError !== TURNSTILE_REQUIRED_MESSAGE ? (
        <p className="nd1-auth-alert" role="alert">
          {submitError}
        </p>
      ) : null}

      <div className={`signup-type${emailLocked ? " signup-type--locked" : ""}`}>
        <p className="signup-type-title">I am...</p>
        <div className="signup-type-radios">
          <label className={`type-option type-option--applicant ${signupType === "applicant" ? "selected" : ""}`}>
            <input
              type="radio"
              name="signupType"
              value="applicant"
              checked={signupType === "applicant"}
              onChange={(event) => setSignupType(event.target.value)}
              disabled={emailLocked}
            />
            <span className="jw-register-type-icon jw-register-type-icon--p1">
              <Search className="nd1-icon" aria-hidden="true" />
            </span>
            <span>Looking for a job</span>
          </label>
          <label className={`type-option type-option--hiring ${signupType === "hiring-manager" ? "selected" : ""}`}>
            <input
              type="radio"
              name="signupType"
              value="hiring-manager"
              checked={signupType === "hiring-manager"}
              onChange={(event) => setSignupType(event.target.value)}
              disabled={emailLocked}
            />
            <span className="jw-register-type-icon jw-register-type-icon--p2">
              <BriefcaseBusiness className="nd1-icon" aria-hidden="true" />
            </span>
            <span>Hiring for a job(s)</span>
          </label>
        </div>
      </div>

      <form
        id="nd1-signup-form"
        className={`nd1-auth-form signup-form${emailLocked ? " signup-form--email-locked" : ""}`}
        action="#"
        method="post"
        onSubmit={onSubmit}
      >
        <label htmlFor="fullName" className={emailLocked ? "signup-label-muted" : undefined}>
          Full name
        </label>
        <div className="nd1-auth-field">
          <input
            id="fullName"
            name="fullName"
            type="text"
            placeholder="Enter your name"
            value={fullName}
            onChange={(event) => setFullName(event.target.value)}
            maxLength={SIGNUP_INPUT_MAX.fullName}
            required
            readOnly={emailLocked}
            className={emailLocked ? "signup-input-readonly" : undefined}
          />
        </div>

        <label htmlFor="email" className={emailLocked ? "signup-label-muted" : undefined}>
          Email address
        </label>
        <div className="nd1-auth-field">
          <input
            id="email"
            name="email"
            type="email"
            placeholder="Enter your email"
            value={email}
            onChange={onEmailChange}
            maxLength={SIGNUP_INPUT_MAX.email}
            required
            readOnly={emailLocked}
            className={emailLocked ? "signup-input-readonly" : undefined}
            aria-readonly={emailLocked}
          />
        </div>
        {needsCompanyFields && !isBusinessEmailValid(email) && email ? (
          <p className="field-note">
            Please use a company email address (public email domains are not allowed).
          </p>
        ) : null}

        <label htmlFor="mobile">Mobile number</label>
        <div className={`mobile-row${emailLocked ? " mobile-row--editable" : ""}`}>
          <select
            id="countryIso"
            name="countryIso"
            value={countryIso}
            onChange={(event) => setCountryIso(event.target.value)}
            required
            aria-label="Country for mobile number"
            aria-describedby={emailLocked ? "signup-mobile-only-hint" : undefined}
          >
            {COUNTRY_DIAL_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
          <input
            id="mobile"
            name="mobile"
            type="tel"
            placeholder="7123 456789"
            value={mobile}
            onChange={(event) => setMobile(event.target.value)}
            maxLength={SIGNUP_INPUT_MAX.mobile}
            required
            aria-describedby={emailLocked ? "signup-mobile-only-hint" : undefined}
          />
        </div>

        {needsCompanyFields ? (
          <>
            <label htmlFor="companyName" className={emailLocked ? "signup-label-muted" : undefined}>
              Company name
            </label>
            <div className="nd1-auth-field">
              <input
                id="companyName"
                name="companyName"
                type="text"
                placeholder="Enter company name"
                value={companyName}
                onChange={(event) => setCompanyName(event.target.value)}
                maxLength={SIGNUP_INPUT_MAX.companyName}
                required
                readOnly={emailLocked}
                className={emailLocked ? "signup-input-readonly" : undefined}
              />
            </div>

            <label htmlFor="companyWebsite" className={emailLocked ? "signup-label-muted" : undefined}>
              Company website
            </label>
            <div className={`signup-url-field${emailLocked ? " signup-url-field--readonly" : ""}`}>
              <span className="signup-url-prefix">{COMPANY_WEBSITE_PREFIX}</span>
              <input
                id="companyWebsite"
                name="companyWebsite"
                type="text"
                inputMode="url"
                autoComplete="url"
                placeholder="www.company.com"
                value={companyWebsite}
                onChange={(event) => setCompanyWebsite(stripLeadingUrlScheme(event.target.value))}
                maxLength={COMPANY_WEBSITE_INPUT_MAX}
                required
                readOnly={emailLocked}
                className={emailLocked ? "signup-input-readonly" : undefined}
              />
            </div>
          </>
        ) : null}

        {!emailLocked ? (
          <label htmlFor="password">Password</label>
        ) : (
          <p className="signup-password-optional-note" id="signup-mobile-only-hint">
            You can update your mobile number and country code — when you&apos;re happy with them, continue to
            the next step.
          </p>
        )}
        {!emailLocked ? (
          <div className="nd1-auth-field nd1-auth-field--password">
            <input
              id="password"
              name="password"
              type={showPassword ? "text" : "password"}
              placeholder="Create a password"
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              maxLength={SIGNUP_INPUT_MAX.password}
              required
            />
            <button
              type="button"
              className="nd1-auth-field__toggle"
              onClick={() => setShowPassword((prev) => !prev)}
              aria-label={showPassword ? "Hide password" : "Show password"}
            >
              {showPassword ? (
                <EyeOff className="nd1-icon" aria-hidden="true" />
              ) : (
                <Eye className="nd1-icon" aria-hidden="true" />
              )}
            </button>
          </div>
        ) : null}

        {!showNd1Turnstile && !emailLocked ? (
          <p className="nd1-signup-verify-note">
            <ShieldCheck className="nd1-icon" aria-hidden="true" />
            Quick verification to help keep JOBUWIN free from fake accounts.
          </p>
        ) : null}

        {!showNd1Turnstile && !emailLocked ? (
          <label className="signup-legal-consent">
            <input
              type="checkbox"
              name="acceptLegal"
              checked={acceptedLegal}
              onChange={(event) => {
                setAcceptedLegal(event.target.checked);
                if (event.target.checked) {
                  setSubmitError((prev) =>
                    prev === "Please agree to the Terms of Use and Privacy Policy to continue."
                      ? ""
                      : prev
                  );
                }
              }}
            />
            <span>
              I agree to the{" "}
              <Link to="/terms" target="_blank" rel="noopener noreferrer">
                Terms of Use
              </Link>{" "}
              and{" "}
              <Link to="/privacy" target="_blank" rel="noopener noreferrer">
                Privacy Policy
              </Link>
              .
            </span>
          </label>
        ) : null}

        {!showNd1Turnstile ? (
          <button type="submit" className="nd1-btn nd1-btn--primary nd1-auth-submit" disabled={submitting}>
            {submitting
              ? emailLocked
                ? "Saving..."
                : "Signing up..."
              : emailLocked
                ? "Save mobile & continue"
                : "Sign up"}
            <ArrowRight className="nd1-icon" aria-hidden="true" />
          </button>
        ) : null}
      </form>

      {showNd1Turnstile ? (
        <div className="signup-form-captcha-sibling nd1-signup-captcha">
          {submitError === TURNSTILE_REQUIRED_MESSAGE ? (
            <p className="nd1-auth-alert" role="alert">
              {submitError}
            </p>
          ) : null}
          <p className="nd1-signup-verify-note">
            <ShieldCheck className="nd1-icon" aria-hidden="true" />
            Quick verification to help keep JOBUWIN free from fake accounts.
          </p>
          <TurnstileField
            id="nd1-signup-turnstile"
            className="nd1-signup-turnstile"
            onTokenChange={(token) => {
              setTurnstileToken(token);
              setTurnstileWidgetError(false);
              if (token) {
                setSubmitError((prev) => (prev === TURNSTILE_REQUIRED_MESSAGE ? "" : prev));
              }
            }}
            onWidgetError={() => setTurnstileWidgetError(true)}
            widgetKey="nd1-signup"
            options={{ action: "signup" }}
          />
          {turnstileWidgetError ? (
            <p className="nd1-auth-alert" role="alert">
              We couldn&apos;t load the security check. Try refreshing the page, or turn off ad blockers for
              this site.
            </p>
          ) : (
            <p className="turnstile-human-note nd1-signup-turnstile-note">
              Security check: making sure you are a genuine human :-)
            </p>
          )}
          <label className="signup-legal-consent">
            <input
              form="nd1-signup-form"
              type="checkbox"
              name="acceptLegal"
              checked={acceptedLegal}
              onChange={(event) => {
                setAcceptedLegal(event.target.checked);
                if (event.target.checked) {
                  setSubmitError((prev) =>
                    prev === "Please agree to the Terms of Use and Privacy Policy to continue."
                      ? ""
                      : prev
                  );
                }
              }}
            />
            <span>
              I agree to the{" "}
              <Link to="/terms" target="_blank" rel="noopener noreferrer">
                Terms of Use
              </Link>{" "}
              and{" "}
              <Link to="/privacy" target="_blank" rel="noopener noreferrer">
                Privacy Policy
              </Link>
              .
            </span>
          </label>
          <button
            type="submit"
            form="nd1-signup-form"
            className="nd1-btn nd1-btn--primary nd1-auth-submit"
            disabled={submitting}
          >
            {submitting ? "Signing up..." : "Sign up"}
            <ArrowRight className="nd1-icon" aria-hidden="true" />
          </button>
        </div>
      ) : null}

      <p className="nd1-auth-switch">
        Already have an account? <Link to={signinHref}>Sign in</Link>
      </p>
    </section>
  );

  if (isPrivatePreviewActive()) {
    return (
      <Nd1AuthShell hideFooter>
        <PageSeo
          title="Sign up"
          description="JOBUWIN sign-ups are opening soon."
          path="/signup"
          canonicalPath="/signup"
          noindex
        />
        <main id="nd1-main" className="nd1-shell nd1-auth-shell nd1-auth-shell--signin">
          <div className="nd1-auth-panel nd1-auth-panel--solo">
            <section className="nd1-auth-card nd1-auth-card--signin">
              <Link className="nd1-auth-back" to="/">
                <ArrowLeft className="nd1-icon" aria-hidden="true" />
                Back to home
              </Link>
              <p className="nd1-auth-card__eyebrow">Sign up</p>
              <h2>Oh oh — we&apos;re not live yet</h2>
              <p className="nd1-auth-card__lede">
                We&apos;re putting the finishing touches on JOBUWIN. Sign-ups will open in just a few
                more days — hang tight.
              </p>
              <p className="nd1-auth-switch">
                Already have an account? <Link to={signinHref}>Sign in</Link>
              </p>
              <p className="nd1-auth-verify-help">
                Questions?{" "}
                <a className="nd1-auth-link-inline" href="mailto:hello@jobuwin.com">
                  Say hello
                </a>
              </p>
            </section>
          </div>
        </main>
      </Nd1AuthShell>
    );
  }

  return (
    <Nd1AuthShell hideFooter>
      <PageSeo
        title="Create your account"
        description="Join JOBUWIN to find genuine jobs, hire real people, and connect through authentic hiring conversations."
        path="/signup"
        canonicalPath="/signup"
        noindex
      />
      <main id="nd1-main" className="nd1-shell nd1-auth-shell nd1-auth-shell--signup">
        <div className="nd1-auth-panel nd1-auth-panel--signup">
          <Nd1SignupMarketing />
          {signupCard}
        </div>
      </main>
    </Nd1AuthShell>
  );
}

export default SignupPage;
